import { apiClient } from './apiClient';

export interface Timezone {
  value: string;
  label: string;
  region: string;
  city: string;
  offset?: string;
  abbreviation?: string;
}

export interface CurrencySymbol {
  symbol: string;
  name: string;
  code: string;
}

export interface TimezoneResponse {
  all: Timezone[];
  popular: Timezone[];
  grouped: { [region: string]: Timezone[] };
}

class TimezoneService {
  /**
   * Get all timezone data
   */
  async getTimezones(): Promise<TimezoneResponse> {
    try {
      const response = await apiClient.get<{
        success: boolean;
        data: TimezoneResponse;
      }>('/settings/timezones');

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error('Failed to fetch timezones');
      }
    } catch (error: any) {
      console.error('Error fetching timezones:', error);
      // Return fallback data
      return {
        all: [],
        popular: [
          { value: 'Asia/Karachi', label: 'Asia/Karachi (PKT)', region: 'Asia', city: 'Karachi' },
          { value: 'UTC', label: 'UTC (UTC)', region: 'UTC', city: 'UTC' },
          { value: 'America/New_York', label: 'America/New York (EST)', region: 'America', city: 'New York' },
          { value: 'Europe/London', label: 'Europe/London (GMT)', region: 'Europe', city: 'London' },
        ],
        grouped: {}
      };
    }
  }

  /**
   * Get popular timezones only
   */
  async getPopularTimezones(): Promise<Timezone[]> {
    const data = await this.getTimezones();
    return data.popular;
  }

  /**
   * Get currency symbols
   */
  async getCurrencySymbols(): Promise<CurrencySymbol[]> {
    try {
      const response = await apiClient.get<{
        success: boolean;
        data: CurrencySymbol[];
      }>('/settings/currency-symbols');

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error('Failed to fetch currency symbols');
      }
    } catch (error: any) {
      console.error('Error fetching currency symbols:', error);
      // Return fallback data
      return [
        { symbol: '₨', name: 'Pakistani Rupee', code: 'PKR' },
        { symbol: '$', name: 'US Dollar', code: 'USD' },
        { symbol: '€', name: 'Euro', code: 'EUR' },
        { symbol: '£', name: 'British Pound', code: 'GBP' },
        { symbol: '¥', name: 'Japanese Yen', code: 'JPY' },
        { symbol: '₹', name: 'Indian Rupee', code: 'INR' },
      ];
    }
  }
}

export const timezoneService = new TimezoneService();
export default timezoneService;